import { createRng, type SeededRng } from '../rng';
import {
  computeSpeedFactor,
  createInitialCooldowns,
  effectiveBaseCd,
  tickCooldowns,
  isCooldownReady,
  tickGlobalCooldown,
} from './cooldown';
import { computeDamage } from './damage';
import { pickStubNpcMove } from './npc-stub';
import { deriveStats } from './stats';
import type {
  ActivePokemon,
  BattleCombatantSetup,
  BattleDataContext,
  BattleInitial,
  BattleSide,
  BattleState,
  BattleStatus,
  CastResult,
  MoveChoice,
  RunSimulatedBattleOptions,
} from './types';

const DEFAULT_MAX_TICKS = 50_000;

function battlerRef(side: BattleSide, slot = 0): string {
  return `${side}:${slot}`;
}

function getActive(state: BattleState, side: BattleSide): ActivePokemon {
  return side === 'player' ? state.player : state.opponent;
}

function opponentOf(side: BattleSide): BattleSide {
  return side === 'player' ? 'opponent' : 'player';
}

function appendEvent(state: BattleState, type: string, payload: Record<string, unknown>): void {
  state.events.push({ t: state.logicalTime, type, payload });
  state.logicalTime += 1;
}

function recordRngRoll(
  state: BattleState,
  rng: SeededRng,
  purpose: string,
  drawIndex: number,
): void {
  appendEvent(state, 'rng-roll', { purpose, drawIndex });
  state.rngOffset = rng.offset;
}

function addUltCharge(
  state: BattleState,
  side: BattleSide,
  delta: number,
  reason: string,
): void {
  if (delta <= 0) return;
  const mon = getActive(state, side);
  const before = mon.ultCharge;
  mon.ultCharge = Math.min(1, mon.ultCharge + delta);
  const applied = mon.ultCharge - before;
  if (applied > 0) {
    appendEvent(state, 'ult-charge', {
      side,
      ultCharge: mon.ultCharge,
      delta: applied,
      reason,
    });
  }
}

function buildActive(
  side: BattleSide,
  setup: BattleCombatantSetup,
  ctx: BattleDataContext,
): ActivePokemon {
  const species = ctx.getSpecies(setup.speciesId);
  const stats = deriveStats(species.baseStats, setup.level);
  return {
    side,
    slot: 0,
    speciesId: setup.speciesId,
    level: setup.level,
    types: species.types,
    moveIds: setup.moveIds,
    currentHp: stats.maxHp,
    stats,
    cooldowns: {
      moveIds: setup.moveIds,
      remainingCd: createInitialCooldowns(setup.moveIds),
    },
    globalRemainingCd: ctx.balance.battleStartGlobalCdSeconds,
    ultCharge: 0,
  };
}

function applyFaintIfNeeded(
  state: BattleState,
  defeatedSide: BattleSide,
  winner: BattleSide,
): BattleStatus | null {
  const mon = getActive(state, defeatedSide);
  if (mon.currentHp > 0) return null;
  appendEvent(state, 'faint', { target: battlerRef(defeatedSide) });
  return winner === 'player' ? 'player-won' : 'opponent-won';
}

function ultChargeForCast(moveSlot: string, balance: BattleDataContext['balance']): number {
  if (moveSlot === 'main') return balance.ultaChargePerMain;
  if (moveSlot === 'secondary1' || moveSlot === 'secondary2') {
    return balance.ultaChargePerSecondary;
  }
  return 0;
}

function resolveMoveEffects(
  state: BattleState,
  rng: SeededRng,
  ctx: BattleDataContext,
  actorSide: BattleSide,
  moveId: string,
): BattleStatus | null {
  const attacker = getActive(state, actorSide);
  const defenderSide = opponentOf(actorSide);
  const defender = getActive(state, defenderSide);
  const move = ctx.getMove(moveId);

  appendEvent(state, 'move-cast', {
    attacker: battlerRef(actorSide),
    moveId,
    targets: [battlerRef(defenderSide)],
    battleTime: state.battleTime,
  });

  if (move.accuracy < 100) {
    const accuracyRoll = rng.next();
    recordRngRoll(state, rng, 'accuracy-check', rng.offset - 1);
    if (accuracyRoll >= move.accuracy / 100) {
      return null;
    }
  }

  const hasDamageAtom = move.effects.some((e) => e.atom === 'damage');
  if (!hasDamageAtom || move.power <= 0) {
    return null;
  }

  const { amount, critical, stab } = computeDamage(attacker, defender, move, ctx.balance, rng, {
    onRngRoll(purpose, drawIndex) {
      recordRngRoll(state, rng, purpose, drawIndex);
    },
  });

  if (amount <= 0) return null;

  const applied = Math.min(defender.currentHp, amount);
  defender.currentHp -= applied;

  addUltCharge(
    state,
    defenderSide,
    (applied / defender.stats.maxHp) * ctx.balance.ultaChargePerDamageTakenPercent,
    'damage-taken',
  );

  appendEvent(state, 'damage', {
    target: battlerRef(defenderSide),
    hpDelta: -applied,
    reason: 'move',
    moveId,
    critical,
    stab,
    battleTime: state.battleTime,
  });

  return applyFaintIfNeeded(state, defenderSide, actorSide);
}

export function createBattle(initial: BattleInitial, ctx: BattleDataContext): BattleState {
  return {
    seed: initial.seed,
    rngOffset: 0,
    battleTime: 0,
    tickIndex: 0,
    player: buildActive('player', initial.player, ctx),
    opponent: buildActive('opponent', initial.opponent, ctx),
    status: 'active',
    events: [],
    logicalTime: 0,
  };
}

export function tick(state: BattleState, dt: number | undefined, ctx: BattleDataContext): BattleState {
  if (state.status !== 'active') return state;

  const step = dt ?? ctx.balance.battleTickSeconds;
  state.battleTime += step;
  state.tickIndex += 1;

  for (const side of ['player', 'opponent'] as const) {
    const mon = getActive(state, side);
    mon.globalRemainingCd = tickGlobalCooldown(mon.globalRemainingCd, step);
    const speedFactor = computeSpeedFactor(mon.stats.speed, ctx.balance);
    tickCooldowns(mon.cooldowns.remainingCd, mon.moveIds, step, speedFactor);
    addUltCharge(state, side, ctx.balance.ultaPassiveChargePerSecond * step, 'passive');
  }

  appendEvent(state, 'tick', {
    tickIndex: state.tickIndex,
    battleTime: state.battleTime,
    dt: step,
  });

  return state;
}

export function tryCastMove(
  state: BattleState,
  side: BattleSide,
  choice: MoveChoice,
  ctx: BattleDataContext,
): CastResult {
  if (state.status !== 'active') {
    return { ok: false, reason: 'battle-ended' };
  }

  const actor = getActive(state, side);
  if (!actor.moveIds.includes(choice.moveId)) {
    return { ok: false, reason: 'illegal-move' };
  }

  if (!isCooldownReady(actor.globalRemainingCd)) {
    return { ok: false, reason: 'global-cooldown' };
  }

  const remaining = actor.cooldowns.remainingCd[choice.moveId] ?? 0;
  if (!isCooldownReady(remaining)) {
    return { ok: false, reason: 'on-cooldown' };
  }

  const rng = createRng(state.seed, state.rngOffset);
  const terminal = resolveMoveEffects(state, rng, ctx, side, choice.moveId);
  state.rngOffset = rng.offset;

  const move = ctx.getMove(choice.moveId);
  const effectiveCd = effectiveBaseCd(move.baseCD, ctx.balance);
  actor.cooldowns.remainingCd[choice.moveId] = effectiveCd;

  appendEvent(state, 'cooldown-applied', {
    side,
    moveId: choice.moveId,
    effectiveCd,
    remainingCd: effectiveCd,
    battleTime: state.battleTime,
  });

  addUltCharge(state, side, ultChargeForCast(move.slot, ctx.balance), 'cast');

  if (terminal) {
    state.status = terminal;
  }

  return { ok: true, battle: state };
}

function sidesBySpeedDesc(state: BattleState): readonly [BattleSide, BattleSide] {
  const p = state.player.stats.speed;
  const o = state.opponent.stats.speed;
  if (p > o) return ['player', 'opponent'];
  if (o > p) return ['opponent', 'player'];
  return ['player', 'opponent'];
}

function stubChoiceForSide(
  state: BattleState,
  side: BattleSide,
  playerChoose: RunSimulatedBattleOptions['playerChoose'],
): MoveChoice | null {
  if (side === 'player' && playerChoose) {
    return playerChoose(state);
  }
  const moveId = pickStubNpcMove(state, side);
  return moveId ? { kind: 'move', moveId } : null;
}

export function runSimulatedBattle(
  initial: BattleInitial,
  ctx: BattleDataContext,
  options: RunSimulatedBattleOptions = {},
): BattleState {
  const maxTicks = options.maxTicks ?? DEFAULT_MAX_TICKS;
  const { playerChoose } = options;
  let state = createBattle(initial, ctx);

  for (let i = 0; i < maxTicks && state.status === 'active'; i += 1) {
    state = tick(state, undefined, ctx);

    for (const side of sidesBySpeedDesc(state)) {
      const choice = stubChoiceForSide(state, side, playerChoose);
      if (!choice) continue;

      const result = tryCastMove(state, side, choice, ctx);
      if (result.ok) {
        state = result.battle;
        if (state.status !== 'active') break;
      }
    }
  }

  return state;
}
